import {
  AlertTriangle,
  Bell,
  Check,
  Download,
  MessageCircle,
  Phone,
  Share2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Modal } from '../../design-system';
import {
  getMessengerV2NotificationPermission,
  requestMessengerV2NotificationPermission,
} from '../../lib/messengerDesktopNotifications.js';
import { useMessengerV2ControllerContext } from '../messenger-v2/MessengerV2ControllerContext.jsx';
import { syncMessengerV2WebPushSubscription } from '../messenger-v2/runtime/webPushSubscriptionService.js';
import s from './PwaOnboarding.module.css';

const INSTALL_DISMISSED_KEY = 'nettgrav:pwa-install-dismissed-session';
const NOTIFICATIONS_DISMISSED_KEY = 'nettgrav:pwa-notifications-dismissed-session';

const isStandaloneDisplay = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.navigator?.standalone === true);

const detectInstallPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const userAgent = String(navigator.userAgent || '').toLowerCase();
  const isTouchMac = /macintosh/.test(userAgent) && Number(navigator.maxTouchPoints) > 1;
  if (/iphone|ipad|ipod/.test(userAgent) || isTouchMac) return 'ios';
  if (/macintosh|mac os x/.test(userAgent) && /safari/.test(userAgent) && !/chrome|crios/.test(userAgent)) {
    return 'mac-safari';
  }
  return 'other';
};

const readSessionDismissal = (key) => {
  try {
    return window.sessionStorage?.getItem(key) === '1';
  } catch {
    return false;
  }
};

const rememberSessionDismissal = (key) => {
  try {
    window.sessionStorage?.setItem(key, '1');
  } catch {}
};

export const PwaOnboarding = () => {
  const intl = useIntl();
  const controller = useMessengerV2ControllerContext();
  const isAuthorized = useSelector((state) => state?.auth?.isAuthorized === true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandaloneDisplay);
  const [permission, setPermission] = useState(getMessengerV2NotificationPermission);
  const [installDismissed, setInstallDismissed] = useState(() =>
    readSessionDismissal(INSTALL_DISMISSED_KEY),
  );
  const [notificationsDismissed, setNotificationsDismissed] = useState(() =>
    readSessionDismissal(NOTIFICATIONS_DISMISSED_KEY),
  );
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const platform = useMemo(detectInstallPlatform, []);
  const callStatus = String(controller?.voiceCall?.status || 'idle').toLowerCase();

  useEffect(() => {
    const timer = window.setTimeout(() => setReady(true), 900);
    const handleInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setInstallDismissed(true);
      setNotificationsDismissed(false);
    };
    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    const media = window.matchMedia?.('(display-mode: standalone)');
    const handleDisplayMode = () => setInstalled(isStandaloneDisplay());
    media?.addEventListener?.('change', handleDisplayMode);
    return () => media?.removeEventListener?.('change', handleDisplayMode);
  }, []);

  useEffect(() => {
    const syncPermission = () => {
      if (document.visibilityState === 'visible') {
        setPermission(getMessengerV2NotificationPermission());
      }
    };
    document.addEventListener('visibilitychange', syncPermission);
    return () => document.removeEventListener('visibilitychange', syncPermission);
  }, []);

  const canExplainManualInstall = platform === 'ios' || platform === 'mac-safari';
  const shouldOfferInstall = !installed && Boolean(installPrompt || canExplainManualInstall);
  const shouldOfferNotifications =
    installed &&
    isAuthorized &&
    permission !== 'granted' &&
    permission !== 'unsupported';
  const step = shouldOfferInstall
    ? 'install'
    : shouldOfferNotifications
      ? 'notifications'
      : null;
  const open =
    ready &&
    callStatus === 'idle' &&
    ((step === 'install' && !installDismissed) ||
      (step === 'notifications' && !notificationsDismissed));

  const dismiss = useCallback(() => {
    const key = step === 'notifications' ? NOTIFICATIONS_DISMISSED_KEY : INSTALL_DISMISSED_KEY;
    rememberSessionDismissal(key);
    if (step === 'notifications') setNotificationsDismissed(true);
    else setInstallDismissed(true);
  }, [step]);

  const install = useCallback(async () => {
    if (!installPrompt) {
      dismiss();
      return;
    }
    setBusy(true);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice?.outcome !== 'accepted') dismiss();
    } finally {
      setInstallPrompt(null);
      setBusy(false);
    }
  }, [dismiss, installPrompt]);

  const enableNotifications = useCallback(async () => {
    setBusy(true);
    try {
      const nextPermission = await requestMessengerV2NotificationPermission();
      setPermission(nextPermission);
      if (nextPermission === 'granted') {
        await syncMessengerV2WebPushSubscription({
          clientId:
            controller?.state?.bootstrap?.clientId ??
            controller?.state?.bootstrap?.identityId ??
            null,
        }).catch(() => null);
        setNotificationsDismissed(true);
      }
    } finally {
      setBusy(false);
    }
  }, [controller?.state?.bootstrap?.clientId, controller?.state?.bootstrap?.identityId]);

  const t = useCallback(
    (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values),
    [intl],
  );

  if (!step) return null;

  const notificationStep = step === 'notifications';
  const manualInstall = !installPrompt;
  const title = notificationStep
    ? t('pwa.onboarding.notifications_title', 'Never miss a message or call')
    : t('pwa.onboarding.install_title', 'Install Nettgrav');

  return (
    <Modal
      open={open}
      title={title}
      ariaLabel={title}
      header={null}
      width={560}
      centered
      closable={false}
      keyboard={false}
      maskClosable={false}
      closeOnOutsideClick={false}
      classNames={{
        modal: s.modal,
        body: s.modalBody,
        footer: s.modalFooter,
        overlay: s.overlay,
      }}
      footer={
        <div className={s.actions}>
          <Button
            variant="secondary"
            size="lg"
            className={s.secondaryAction}
            onClick={dismiss}
            disabled={busy}
          >
            {t('pwa.onboarding.not_now', 'Not now')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className={s.primaryAction}
            icon={
              notificationStep ? <Bell size={18} aria-hidden /> : <Download size={18} aria-hidden />
            }
            onClick={
              notificationStep && permission === 'denied'
                ? dismiss
                : notificationStep
                  ? enableNotifications
                  : install
            }
            loading={busy}
          >
            {notificationStep
              ? permission === 'denied'
                ? t('pwa.onboarding.install_manual_action', 'I understand')
                : t('pwa.onboarding.notifications_action', 'Allow notifications')
              : manualInstall
                ? t('pwa.onboarding.install_manual_action', 'I understand')
                : t('pwa.onboarding.install_action', 'Install Nettgrav')}
          </Button>
        </div>
      }
    >
      <div className={s.content}>
        <div className={`${s.hero} ${notificationStep ? s.notificationHero : ''}`}>
          <div className={s.heroGlow} aria-hidden />
          <div className={s.appIconFrame} aria-hidden>
            <img src="/favicon-192.png" alt="" className={s.appIcon} />
            <span className={s.appIconStatus} />
          </div>
          <div className={s.heroCopy}>
            <span className={s.eyebrow}>Nettgrav</span>
            <h2>{title}</h2>
            <p>
              {notificationStep
                ? t(
                    'pwa.onboarding.notifications_body',
                    'Allow Nettgrav to notify you about new encrypted messages and incoming calls, even when the app is closed.',
                  )
                : t(
                    'pwa.onboarding.install_body',
                    'Keep Nettgrav one tap away and receive a more reliable app experience on this device.',
                  )}
            </p>
          </div>
        </div>

        <div className={s.details}>
          <ul className={s.benefits}>
            <li>
              <span className={s.benefitIcon} aria-hidden>
                <MessageCircle size={18} />
              </span>
              <span className={s.benefitText}>
                {t('pwa.onboarding.benefit_messages', 'Background message notifications')}
              </span>
              <Check className={s.benefitCheck} size={17} aria-hidden />
            </li>
            <li>
              <span className={s.benefitIcon} aria-hidden>
                <Phone size={18} />
              </span>
              <span className={s.benefitText}>
                {t('pwa.onboarding.benefit_calls', 'Incoming call alerts')}
              </span>
              <Check className={s.benefitCheck} size={17} aria-hidden />
            </li>
            <li>
              <span className={s.benefitIcon} aria-hidden>
                <Bell size={18} />
              </span>
              <span className={s.benefitText}>
                {t('pwa.onboarding.benefit_badge', 'Unread count on the app icon')}
              </span>
              <Check className={s.benefitCheck} size={17} aria-hidden />
            </li>
          </ul>

          {!notificationStep && manualInstall ? (
            <div className={s.instructions}>
              <span className={s.instructionIcon} aria-hidden>
                <Share2 size={21} />
              </span>
              <p>
                {platform === 'ios'
                  ? t(
                      'pwa.onboarding.ios_steps',
                      'Tap Share in your browser, choose Add to Home Screen, then open Nettgrav from its new icon.',
                    )
                  : t(
                      'pwa.onboarding.mac_steps',
                      'In Safari, choose File → Add to Dock, then open Nettgrav from the Dock or Applications.',
                    )}
              </p>
            </div>
          ) : null}

          {notificationStep && permission === 'denied' ? (
            <div className={s.denied}>
              <AlertTriangle size={20} aria-hidden />
              <p>
                {t(
                  'pwa.onboarding.notifications_denied',
                  'Notifications are currently blocked. Enable them for Nettgrav in your browser or device notification settings, then try again.',
                )}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default PwaOnboarding;
